import { useAlgoliaSearchClient } from '@algolia-search/composables/useAlgoliaSearchClient';
import { INDEX_PRODUCTS } from '@algolia-search/consts';
import { useAlgoliaConfigStore } from '@algolia-search/stores/config';
import type { AlgoliaFacet, AlgoliaSortByItems, AlgoliaSorting } from '@algolia-search/types';
import type { ComputedRef, Ref } from '@nuxtjs/composition-api';
import { useContext } from '@nuxtjs/composition-api';
import { computed, ref, useMeta } from '@nuxtjs/composition-api';

import { Logger } from '~/helpers/logger';
import type { UseContextReturn } from '~/types/core';

import type { TopSearchesItem } from './types';

export const useAlgoliaSearch = (): {
  connectAlgoliaScript: () => Promise<void>;
  fetchTopSearches: () => Promise<void>;
  topSearches: Ref<TopSearchesItem[] | null>;
  isTopSearchesLoading: Ref<boolean>;
  getIndex: (indexName: string, isContentfulIndex?: boolean) => string | null;
  sortConfig: ComputedRef<AlgoliaSortByItems[]>;
  facets: Record<string, AlgoliaFacet>;
} => {
  const { searchClient, clientConfig } = useAlgoliaSearchClient(true);
  const {
    $algoliaSearch: { envName, contentfulPagesEnvName },
    app: { i18n }
  } = useContext();
  const topSearches = ref<TopSearchesItem[] | null>(null);
  const isTopSearchesLoading: Ref<boolean> = ref(true);
  const localeCode: string = i18n.localeProperties.code;
  const { sorts, facets } = useAlgoliaConfigStore();
  const US_REGION = 'us';
  const DE_REGION = 'de';
  const ANALYTICS_MAPPING = {
    en_us: US_REGION,
    fr_us: US_REGION,
    en_hk: US_REGION,
    zh_hk: US_REGION,
    ja_jp: US_REGION,
    fr_fr: DE_REGION,
    en_eu: DE_REGION,
    fr_eu: DE_REGION,
    es_eu: DE_REGION,
    it_eu: DE_REGION,
    de_eu: DE_REGION,
    en_uk: DE_REGION
  } as const;
  const analyticsRegion = ANALYTICS_MAPPING[localeCode] ?? 'de';

  const context: UseContextReturn = useContext();

  const connectAlgoliaScript = async (): Promise<void> => {
    if (searchClient.appId) {
      useMeta({
        link: [
          {
            rel: 'preconnect',
            href: `https://${searchClient.appId}-dsn.algolia.net`,
            crossorigin: 'true'
          }
        ]
      });
    }
  };

  function getIndex(indexName: string, isContentfulIndex = false): string | null {
    const envPrefix = isContentfulIndex ? contentfulPagesEnvName : envName;
    return envPrefix && indexName ? `${envPrefix}_${localeCode}_${indexName}` : null;
  }

  const fetchTopSearches = async (): Promise<void> => {
    try {
      topSearches.value = await context.$vsf.$algolia.api.getTopSearches({
        appId: clientConfig.appId,
        apiKey: clientConfig.analyticsApiKey,
        localeCode,
        analyticsRegion
      });
    } catch (error) {
      Logger.error('[fetchTopSearches] Failed:', error);
      topSearches.value = null;
    } finally {
      isTopSearchesLoading.value = false;
    }
  };

  const generateSortIndexName = (sortRule: AlgoliaSorting) => {
    // eslint-disable-next-line prefer-const
    let { attribute } = sortRule;

    if (attribute === 'price') {
      attribute += '_default';
    }

    return getIndex([INDEX_PRODUCTS, attribute, sortRule.sort].join('_'));
  };

  const getSortConfig = (sortStoreConfig: Record<string, AlgoliaSorting>): AlgoliaSortByItems[] => {
    const sortConfig: AlgoliaSortByItems[] = Object.values(sortStoreConfig).map((sorting) => ({
      value: generateSortIndexName(sorting),
      label: sorting.sortLabel
    }));
    // push default sorting
    sortConfig.unshift({
      label: 'Default',
      value: getIndex(INDEX_PRODUCTS)
    });

    return sortConfig;
  };

  return {
    sortConfig: computed(() => getSortConfig(sorts)),
    facets,
    connectAlgoliaScript,
    fetchTopSearches,
    getIndex,
    topSearches: computed(() => topSearches.value),
    isTopSearchesLoading: computed(() => isTopSearchesLoading.value)
  };
};
